import { useEffect, useState } from "react";
import { Bishop } from "../../interfaces/bishop";
import { useNavigate, useParams } from "react-router-dom";
import { getStates } from "../../services/helpers";
import {
  deleteBishop,
  getBishop,
  updateBishop,
} from "../../services/bishopService";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { Denomination } from "../../interfaces/denomination";
import { getDenominations } from "../../services/denominationService";
import ToolTip from "../../components/component-tools/ToolTip";

const ViewBishop = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bishop, setBishop] = useState<Bishop>();
  const { bishopID } = useParams();
  const states = getStates();
  const [editing, setEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [denominations, setDenominations] = useState<Denomination[]>([]);

  useEffect(() => {
    const fetchBishopData = async () => {
      try {
        setLoading(true);
        if (bishopID) {
          const numericBishopID = parseInt(bishopID, 10);
          const bishopResponse = await getBishop(numericBishopID);
          setBishop(bishopResponse);
        }

        const denominationsResponse = await getDenominations();
        setDenominations(denominationsResponse);
      } catch {
        console.log("Error fetching bishop");
      } finally {
        setLoading(false);
      }
    };
    fetchBishopData();
  }, [bishopID]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof Bishop,
  ) => {
    setEditing(true);
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (bishop) {
      setBishop({
        ...bishop,
        [field]: value,
      });
    }
  };

  const handleUpdateBishop = async () => {
    setLoadingSpinner(true);
    try {
      if (bishop) {
        await updateBishop(bishop);
        setEditing(false);
        setSuccessMessage("Bishop updated successfully.");
      }
    } catch (error) {
      console.error("Error updating:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDeleteBishop = async () => {
    if (!bishop || !bishop.bishopID) {
      setErrorMessage("Invalid bishop selection.");
      return;
    }

    setLoadingSpinner(true);
    try {
      await deleteBishop(bishop.bishopID);
      navigate("/admin/master-lists/bishops");
    } catch (error) {
      console.error("Error deleting bishop:", error);
      setErrorMessage("Failed to delete bishop. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      {!loading && (
        <div className="header-container">
          <div
            className="page-items-container"
            style={{ alignItems: "center" }}
          >
            <h1>{bishop?.bishopName}</h1>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Organization Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.diocese || ""}
                  onChange={(e) => handleInputChange(e, "diocese")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Denomination:</label>
              <div className="field-input">
                <select
                  className="form-control"
                  value={bishop?.denomination || ""}
                  onChange={(e) => handleInputChange(e, "denomination")}
                >
                  <option value="">Select a Denomination</option>
                  {denominations.map((denomination) => (
                    <option
                      key={denomination.denominationID}
                      value={denomination.denominationName}
                    >
                      {denomination.denominationName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Client Abbreviation:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.bishopName || ""}
                  onChange={(e) => handleInputChange(e, "bishopName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Contact:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.contact || ""}
                  onChange={(e) => handleInputChange(e, "contact")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Greeting:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.greeting || ""}
                  onChange={(e) => handleInputChange(e, "greeting")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">State:</label>
              <select
                className="form-select"
                value={bishop?.state || ""}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
            </div>
            {bishop?.state === "INT" && (
              <div className="field-stacked" style={{ flex: "0 0 15%" }}>
                <label className="field-label">Country:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={bishop?.country || ""}
                    onChange={(e) => handleInputChange(e, "country")}
                  />
                </div>
              </div>
            )}
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">ZipCode:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Email:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.email || ""}
                  onChange={(e) => handleInputChange(e, "email")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={bishop?.phone || ""}
                  onChange={(e) => handleInputChange(e, "phone")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Send Certified:</label>
                <ToolTip
                  tooltipText="Controls if csv box is visible on the reference tracking page once used to build a reference."
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    bishop?.sendCertified ? bishop?.sendCertified : false
                  }
                  onChange={(e) => handleInputChange(e, "sendCertified")}
                />
              </div>
            </div>
            {/* <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Verified:</label>
              </div>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={bishop?.verified}
                  onChange={(e) => handleInputChange(e, "verified")}
                />
              </div>
            </div> */}
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label className="field-label">Comments:</label>
              <div className="field-input">
                <textarea
                  className="form-control"
                  value={bishop?.comments || ""}
                  onChange={(e) => handleInputChange(e, "comments")}
                  style={{ height: "100px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {bishop?.comments?.length ?? 0}/4000
                </div>
              </div>
            </div>

            <div
              style={{
                flex: "0 0 100%",
                alignSelf: "end",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                gap: "20px",
              }}
            >
              <button
                className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
                onClick={handleUpdateBishop}
                disabled={!editing}
              >
                Save
              </button>
              <div style={{ flex: "0 0 10%" }}>
                <button
                  className="oxbtn btn-red"
                  onClick={() => setShowConfirmation(true)}
                >
                  Delete
                </button>
              </div>
              {showConfirmation && (
                <ConfirmDelete
                  onConfirm={() => handleDeleteBishop()}
                  onCancel={() => setShowConfirmation(false)}
                  message="Are you sure you want to delete this bishop?"
                />
              )}
            </div>
          </div>
        </>
      )}

      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ViewBishop;
